import { useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { useQuery } from "@tanstack/react-query";

import { initUseQuery } from "simplydo/core/query";
import { OpenAPI } from "simplydo/interfaces";
import { useCachedState } from "simplydo/react/hooks";
import { ButtonLink } from "simplydo/react/ui/button";
import { H4 } from "simplydo/react/ui/header";
import { Message } from "simplydo/react/ui/message";

import api from "api";
import { useCurrentUser, userCache } from "utils/currentUser";
import util from "utils/utils";

import { Discussion } from "components/lib/Discussion/Discussion";
import type { Comment } from "components/lib/Discussion/types";
import { PREVIEW_IDEA_ID } from "components/lib/Editors/types";

import { useIdea } from "./hooks/useIdea";
import type {
  IdeaCommentDraft,
  IdeaCommentMediaItem,
  IdeaCommentReplyDrafts,
} from "./types";

type CommentsProps = { forId: string; isViewingMergedIdea?: boolean };

const EMPTY_COMMENT_DRAFT: IdeaCommentDraft = { text: "", media: [] };
const EMPTY_REPLY_DRAFTS: IdeaCommentReplyDrafts = {};

const IdeaComments = ({ forId, isViewingMergedIdea }: CommentsProps) => {
  const [idea] = useIdea();
  const user = useCurrentUser();
  const { t } = useTranslation();

  const [commentLoading, setCommentLoading] = useState(false);
  const [page, setPage] = useState(1);
  const userId = user?._id;

  const commentDraftKey =
    forId && userId
      ? { kind: "ideaComment", forType: "idea", forId, user: userId }
      : undefined;
  const replyDraftKey =
    forId && userId
      ? { kind: "ideaCommentReply", forType: "idea", forId, user: userId }
      : undefined;

  const [commentText, setCommentText] = useCachedState<string>(
    commentDraftKey,
    "",
  );
  const [commentMedia, setCommentMedia] = useState<IdeaCommentMediaItem[]>([]);
  const [replies, setReplies] = useCachedState<IdeaCommentReplyDrafts>(
    replyDraftKey,
    EMPTY_REPLY_DRAFTS,
  );

  const commentQuery = useQuery(
    initUseQuery(api.ideas.getComments, [forId, { page }], {
      isEnabled:
        !!forId &&
        forId !== PREVIEW_IDEA_ID &&
        !!user &&
        !idea?.ownerChallenge?.preventIdeaComments,
    }),
  );

  const onPageChange = (page: number) => setPage(page);

  const handlePost = (
    data: { message: string; media?: IdeaCommentMediaItem[]; parent?: string },
    parentCommentId = "",
  ) => {
    setCommentLoading(true);

    const media = data.media?.map((item) => ({
      value: item.value,
      name: item.name,
      type: item.type,
      ...(item.data ? { data: item.data as Record<string, never> } : {}),
    }));

    api.ideas.postComment(
      forId,
      {
        message: data.message.replace("<?insert!>", ""),
        ...(media?.length ? { media } : {}),
        ...(data.parent ? { parent: data.parent } : {}),
      },
      () => {
        toast.success(t("ideas.comments.posted"));
        api.invalidateQueries(api.ideas.get, [idea?._id]);

        userCache.onFollow("idea", forId);
        setCommentLoading(false);
        if (parentCommentId) {
          setReplies((prevState) => {
            const next = { ...prevState };
            delete next[parentCommentId];
            return next;
          });
          api.invalidateQueries(api.ideas.getComments, [forId, { page }]);
        } else {
          setCommentText("");
          setCommentMedia([]);

          api.invalidateQueries(api.ideas.getComments, [forId, { page }]);
        }
      },
      (err) => {
        setCommentLoading(false);
        toast.error(err.message);
      },
    );
  };

  const post = () => {
    if (commentText || commentMedia.length) {
      const data = { message: commentText, media: commentMedia };
      handlePost(data);
    }
  };

  const postReply = (parentCommentId: string) => {
    const replyData = replies[parentCommentId];
    if (replyData?.text || replyData?.media?.length) {
      const data = {
        message: replyData.text ?? "",
        media: replyData.media,
        parent: parentCommentId,
      };
      handlePost(data, parentCommentId);
    }
  };

  const deleteComment = (commentId: string) => {
    return util
      .confirm(
        t("ideas.comments.delete.title"),
        t("ideas.comments.delete.info"),
      )
      .then(() => {
        api.ideas.deleteComment(
          forId,
          commentId,
          () => {
            api.setQueryData(
              api.ideas.getComments,
              [forId, { page }],
              (prev) => ({
                ...prev,
                comments: prev.comments.filter((c) => c._id !== commentId),
              }),
            );
            api.invalidateQueries(api.ideas.get, [idea?._id]);
            toast(t("ideas.comments.delete.success"));
          },
          (err) => toast.error(err.message),
        );
      })
      .catch(() => {});
  };

  const handleAddAttachment = (
    attachment: IdeaCommentMediaItem,
    parentCommentId = "",
  ) => {
    if (parentCommentId) {
      setReplies((prevState) => ({
        ...prevState,
        [parentCommentId]: {
          text: prevState[parentCommentId]?.text ?? "",
          media: [...(prevState[parentCommentId]?.media || []), attachment],
        },
      }));
    } else {
      setCommentMedia((prev) => [...(prev || []), attachment]);
    }
  };

  const addAttachment = (
    storedName: string,
    name: string,
    signedDownloadRequest: string,
    parentId?: string,
  ) => {
    const attachment = {
      value: storedName,
      name,
      signedDownloadRequest,
      type: name.match(/\.(jpg)|(png)|(gif)|(jpeg)$/i)
        ? ("image" as const)
        : ("file" as const),
    };
    handleAddAttachment(attachment, parentId);
  };

  const addGiphy = (
    gif: { url: string; title: string },
    parentCommentId = "",
  ) => {
    const attachment = {
      value: gif.url,
      name: gif.title,
      type: "gif" as const,
      data: gif,
    };
    handleAddAttachment(attachment, parentCommentId);
  };

  const deleteAttachment = (index: number, parentCommentId = "") => {
    if (parentCommentId) {
      setReplies((prev) => ({
        ...prev,
        [parentCommentId]: {
          text: prev[parentCommentId]?.text ?? "",
          media: (prev[parentCommentId]?.media ?? []).filter(
            (_, i) => i !== index,
          ),
        },
      }));
    } else {
      setCommentMedia((prev) => prev?.filter((_, i) => i !== index));
    }
  };

  const getFile = (att: IdeaCommentMediaItem) =>
    att.signedDownloadRequest || att.value;

  const updateComment = (commentText: string) => {
    setCommentText(commentText);
  };

  const updateReply = (parentCommentId: string, replyText: string) => {
    setReplies((prevState) => ({
      ...prevState,
      [parentCommentId]: {
        text: replyText,
        media: prevState[parentCommentId]?.media ?? [],
      },
    }));
  };

  const pin = (commentId: string) => {
    api.ideas.pinComment(
      idea?._id,
      commentId,
      true,
      () => {
        api.invalidateQueries(api.ideas.getComments, [forId]);
      },
      (err) => toast.error(err.message),
    );
  };

  const unpin = (commentId: string) => {
    api.ideas.pinComment(
      idea?._id,
      commentId,
      false,
      () => {
        api.invalidateQueries(api.ideas.getComments, [forId]);
      },
      (err) => toast.error(err.message),
    );
  };

  const reactComment = (commentId: string, emoji: string, adding: boolean) => {
    const func = adding
      ? api.ideas.commentAddReaction
      : api.ideas.commentRemoveReaction;
    func(
      idea?._id,
      commentId,
      emoji,
      (data) => {
        api.setQueryData(api.ideas.getComments, [forId, { page }], (prev) => ({
          ...prev,
          comments: prev.comments.map((c) =>
            c._id === commentId
              ? {
                  ...c,
                  reactCount: data?.reacts?.length || 0,
                  reacts: data.reacts,
                }
              : c,
          ),
        }));
      },
      () => {},
    );
  };

  const isAssessor = idea?.assessments?.find((a) => a.user === userId);

  return (
    <div data-testid="idea-comments">
      {isAssessor && (
        <Message color="orange" size="small">
          {`These comments are visible to anyone with access to this ${t("generic.idea")}. This includes the authors, ${t("generic.challenge")} managers, any assessors, and anyone else who has the link.`}
        </Message>
      )}
      {user ? (
        <Discussion
          getReplyData={(parentCommentId) => {
            const reply = replies[parentCommentId] ?? EMPTY_COMMENT_DRAFT;
            return {
              text: reply.text,
              media: reply.media as NonNullable<
                OpenAPI.Schemas["IdeaComment"]["media"]
              >,
            };
          }}
          canManage={
            util.canManageChallenge(user, idea?.ownerChallenge) ||
            util.hasPermission(
              user,
              "challenge.manageIdeaComments",
              idea?.challenge,
            )
          }
          post={post}
          deleteComment={deleteComment}
          feed={commentQuery.data?.comments || []}
          loading={commentQuery.isLoading}
          type={"idea"}
          updateComment={updateComment}
          commentLoading={commentLoading}
          commentText={commentText}
          commentMedia={commentMedia}
          getFile={(media) => getFile(media as IdeaCommentMediaItem)}
          fileChooserForType="idea"
          fileChooserForId={forId}
          addGiphy={addGiphy}
          addAttachment={addAttachment}
          deleteAttachment={deleteAttachment}
          canPost={true}
          pin={pin}
          unpin={unpin}
          postReply={postReply}
          updateReply={updateReply}
          reactComment={reactComment}
          approveComment={() => {}}
          rejectComment={() => {}}
          placeholder={t("ideas.comments.delete.write")}
          postText={t("ideas.comments.post")}
          canPin={
            util.canManageChallenge(user, idea?.ownerChallenge) ||
            util.hasPermission(
              user,
              "challenge.manageIdeaComments",
              idea?.challenge,
            )
          }
          disableAttachments={false}
          noCommentsText=""
          page={page}
          nextPageAvailable={commentQuery.data?.nextPageAvailable || false}
          previousPageAvailable={
            commentQuery.data?.previousPageAvailable || false
          }
          total={commentQuery.data?.total || 0}
          limit={999}
          onPageChange={onPageChange}
          message={
            idea?.parent && isViewingMergedIdea
              ? `You are commenting on the ${t("generic.idea")} "${idea?.name}"`
              : undefined
          }
        />
      ) : (
        <Message>
          <H4>Please log in to view comments</H4>
          <p>
            Register or login to your account to view and interact with comments
            on this {t("generic.idea")}.
          </p>
          <ButtonLink color="primary" to={`/login?then=/ideas/${idea?._id}`}>
            Login or register
          </ButtonLink>
        </Message>
      )}
    </div>
  );
};

export default IdeaComments;
